import React, { useState } from 'react';
import { Document, Page } from 'react-pdf';
import { DocumentItem } from '../../../services/documentApi';
import ComparisonTable from './ComparisonTable';
import PdfPreviewModal from './PdfPreviewModal';
import './ClassicView.scss';

interface ClassicViewProps {
    invoice: DocumentItem;
    quote: DocumentItem | null;
    isEditing?: boolean;
    editedValues?: Record<string, string>;
    onValueChange?: (entityType: string, value: string) => void;
}

interface PdfThumbnailProps {
    documentId: string | null;
    title: string;
    noDocumentMessage?: string;
    onClick: () => void;
}

const PdfThumbnail: React.FC<PdfThumbnailProps> = ({
    documentId,
    title,
    noDocumentMessage,
    onClick
}) => {
    if (!documentId) {
        return (
            <div className="pdf-thumbnail pdf-thumbnail--empty" onClick={onClick}>
                <div className="pdf-thumbnail__header">{title}</div>
                <div className="pdf-thumbnail__placeholder">
                    <span>{noDocumentMessage || 'No document'}</span>
                </div>
            </div>
        );
    }

    const fileUrl = `http://localhost:5003/api/documents/${documentId}/download?token=${localStorage.getItem('auth_token')}`;

    return (
        <div className="pdf-thumbnail" onClick={onClick}>
            <div className="pdf-thumbnail__header">{title}</div>
            <div className="pdf-thumbnail__preview">
                <Document
                    file={fileUrl}
                    loading={<div className="pdf-thumbnail__loading">Loading...</div>}
                    error={<div className="pdf-thumbnail__error">PDF not found</div>}
                >
                    <Page
                        pageNumber={1}
                        scale={0.5}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                    />
                </Document>
                <div className="pdf-thumbnail__overlay">
                    <span>Click to expand</span>
                </div>
            </div>
        </div>
    );
};

/**
 * Classic View Layout:
 * - Top: Clickable PDF thumbnails - opens modal with full-size PDFs
 * - Bottom: Comparison Table with AI Analysis
 */
const ClassicView: React.FC<ClassicViewProps> = ({
    invoice,
    quote,
    isEditing,
    editedValues,
    onValueChange
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    return (
        <div className="classic-view">
            {/* PDF Thumbnails Row - Clickable */}
            <div className="classic-view__pdf-row">
                <PdfThumbnail
                    documentId={invoice.id}
                    title="Invoice"
                    noDocumentMessage="Invoice PDF not available"
                    onClick={openModal}
                />
                <PdfThumbnail
                    documentId={quote?.id || null}
                    title="Quote / RFQ"
                    noDocumentMessage={
                        invoice.comparison?.linked_quote_id
                            ? `Linked: ${invoice.comparison.linked_quote_id.slice(-8)}`
                            : 'No linked quote'
                    }
                    onClick={openModal}
                />
            </div>

            {/* Table Section */}
            <div className="classic-view__table-section">
                <ComparisonTable
                    invoice={invoice}
                    quote={quote}
                    isEditing={isEditing}
                    editedValues={editedValues}
                    onValueChange={onValueChange}
                />
            </div>

            {/* Full-size PDF Modal */}
            <PdfPreviewModal
                isOpen={isModalOpen}
                onClose={closeModal}
                invoiceId={invoice.id}
                quoteId={quote?.id || null}
                invoiceFilename={invoice.filename}
                quoteFilename={quote?.filename}
            />
        </div>
    );
};

export default ClassicView;
